import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { Plus, Pencil, Trash2, Search, UserPlus, Phone, Mail, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

const ROLE_TYPE_OPTIONS = [
  { value: 'panel_admin',   label: 'Panel Admin' },
  { value: 'manage_admin',  label: 'Manage Admin' },
];

const initForm = { name:'', email:'', phone:'', password:'', panels:[], permissions:{}, is_active:1 };

export default function SuperAdminAdmins() {
  const { api } = useAuth();
  const [admins,      setAdmins]      = useState([]);
  const [panels,      setPanels]      = useState([]);
  const [permTypes,   setPermTypes]   = useState([]);
  const [search,      setSearch]      = useState('');
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(null);
  const [form,        setForm]        = useState(initForm);
  const [editId,      setEditId]      = useState(null);
  const [delId,       setDelId]       = useState(null);
  const [delLoading,  setDelLoading]  = useState(false);
  const [filterRole,  setFilterRole]  = useState('all');
  const [panelModules,setPanelModules]= useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, p, pt] = await Promise.all([
        api.get('/superadmin/admins'),
        api.get('/superadmin/panels'),
        api.get('/superadmin/permission-types'),
      ]);
      setAdmins(a.data); setPanels(p.data); setPermTypes(pt.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const loadModulesForPanel = async (pid) => {
    if (panelModules[pid]) return;
    try {
      const res = await api.get(`/superadmin/panels/${pid}/modules`);
      setPanelModules(prev => ({ ...prev, [pid]: res.data }));
      setForm(f => {
        const p = { ...f.permissions };
        res.data.forEach(m => { if (!p[m.id]) p[m.id] = []; });
        return { ...f, permissions: p };
      });
    } catch {}
  };

  const openCreate = () => { setForm(initForm); setEditId(null); setModal('form'); };

  const openEdit = async a => {
    try {
      const res = await api.get('/superadmin/admins/' + a.id);
      const detail = res.data;
      const pMap = {};
      if (detail.permissions) {
        detail.permissions.forEach(p => { pMap[p.module_id + '_' + p.role_type] = p.permission_type_ids || []; });
      }
      setForm({
        name: detail.name, email: detail.email, phone: detail.phone||'',
        password: '', panels: detail.panels || [], permissions: pMap, is_active: detail.is_active,
      });
      setEditId(detail.id);
      setModal('form');
      if (detail.panels) detail.panels.forEach(p => loadModulesForPanel(p.panel_id));
    } catch { toast.error('Failed to load details'); }
  };

  const togglePanel = pid => {
    setForm(f => {
      const exists = f.panels.find(x => x.panel_id === pid);
      if (exists) {
        return { ...f, panels: f.panels.filter(x => x.panel_id !== pid) };
      } else {
        loadModulesForPanel(pid);
        return { ...f, panels: [...f.panels, { panel_id: pid, role_types: ['panel_admin'] }] };
      }
    });
  };

  // Toggle one role type in the multi-select for a panel
  const togglePanelRoleType = (pid, roleType) => {
    setForm(f => ({
      ...f,
      panels: f.panels.map(x => {
        if (x.panel_id !== pid) return x;
        const types = x.role_types || [];
        return {
          ...x,
          role_types: types.includes(roleType)
            ? types.filter(t => t !== roleType)
            : [...types, roleType],
        };
      }),
    }));
  };

  // Deprecated direct toggle (replaced by inline toggles with composite keys)
  const togglePerm = (mid, roleType, permTypeId) => {
    const key = `${mid}_${roleType}`;
    setForm(f => {
      const current = f.permissions[key] || [];
      return {
        ...f,
        permissions: {
          ...f.permissions,
          [key]: current.includes(permTypeId)
            ? current.filter(id => id !== permTypeId)
            : [...current, permTypeId],
        },
      };
    });
  };

  const toggleAllPerms = (mid, roleType, enable) => {
    const key = `${mid}_${roleType}`;
    setForm(f => ({
      ...f,
      permissions: {
        ...f.permissions,
        [key]: enable ? permTypes.map(pt => pt.id) : [],
      },
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const payload = { ...form };
    if (editId && !payload.password) delete payload.password;
    payload.permissions = Object.entries(payload.permissions).map(([key, ptIds]) => {
      const [mid, roleType] = key.split('_');
      return {
        module_id: Number(mid),
        role_type: roleType,
        permission_type_ids: ptIds,
      };
    }).filter(p => {
      // Only keep permissions for roles that are actually assigned
      const isAssigned = payload.panels.some(panel => panel.role_types.includes(p.role_type));
      return isAssigned && p.permission_type_ids.length > 0;
    });
    try {
      if (editId) { await api.put('/superadmin/admins/' + editId, payload); toast.success('Admin updated'); }
      else        { await api.post('/superadmin/admins', payload);           toast.success('Admin created'); }
      setModal(null); load();
    } catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
  };

  const handleDelete = async () => {
    setDelLoading(true);
    try { await api.delete('/superadmin/admins/' + delId); toast.success('Deleted'); setDelId(null); load(); }
    catch (err) { toast.error(err?.response?.data?.message || 'Error'); }
    finally { setDelLoading(false); }
  };

  const filtered = admins.filter(a => {
    const matchSearch = a.name.toLowerCase().includes(search.toLowerCase()) || a.email.toLowerCase().includes(search.toLowerCase());
    const matchRole   = filterRole === 'all' || a.panels?.some(p => (p.role_types || []).includes(filterRole));
    return matchSearch && matchRole;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Admins</h1>
          <p className="page-subtitle">Manage Panel Admins and Manage Admins — assign them to panels with multi-select roles</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><UserPlus size={16}/> New Admin</button>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <div className="search-bar" style={{ flex:1, minWidth:200 }}>
          <Search size={15} color="var(--text-muted)"/>
          <input placeholder="Search admins…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <select className="form-select" style={{ width:180 }} value={filterRole} onChange={e=>setFilterRole(e.target.value)}>
          <option value="all">All Role Types</option>
          <option value="panel_admin">Panel Admin</option>
          <option value="manage_admin">Manage Admin</option>
        </select>
      </div>

      {loading ? (
        <div className="flex-center" style={{padding:60}}><span className="spinner" style={{width:32,height:32}}/></div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty-state"><Shield size={40}/><h3>No admins found</h3><p>Create your first admin and assign them panels</p><button className="btn btn-primary mt-4" onClick={openCreate}><Plus size={15}/> Create Admin</button></div></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Admin</th><th>Contact</th><th>Assigned Panels &amp; Roles</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="user-avatar" style={{ width:36,height:36,fontSize:'.8rem',flexShrink:0 }}>
                        {a.name?.split(' ')?.map(n=>n[0])?.join('')?.slice(0,2)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <div style={{ fontWeight:600 }}>{a.name}</div>
                        <div className="text-muted text-sm">ID #{a.id}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2 text-sm" style={{marginBottom:2}}><Mail size={12} color="var(--text-muted)"/>{a.email}</div>
                    {a.phone && <div className="flex items-center gap-2 text-sm text-muted"><Phone size={12} color="var(--text-muted)"/>{a.phone}</div>}
                  </td>
                  <td>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {a.panels && a.panels.length > 0 ? a.panels.map((p,i) => (
                        <span key={i} className="badge badge-panel" style={{ fontSize:'.7rem', display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
                          {p.panel_name}
                          <span style={{ display:'flex', gap:2 }}>
                            {(p.role_types || []).map(rt => (
                              <span key={rt} className={`role-badge ${rt === 'panel_admin' ? 'role-panel-admin' : 'role-manage-admin'}`}
                                style={{fontSize:'.6rem', padding:'1px 4px', background:'var(--bg-base)'}}>
                                {rt === 'panel_admin' ? 'PA' : 'MA'}
                              </span>
                            ))}
                          </span>
                        </span>
                      )) : <span className="text-muted text-sm">None</span>}
                    </div>
                  </td>
                  <td><span className={`badge ${a.is_active ? 'badge-active':'badge-inactive'}`}>{a.is_active?'Active':'Inactive'}</span></td>
                  <td>
                    <div className="flex gap-2">
                      <button className="btn btn-ghost btn-sm btn-icon" title="Edit" onClick={()=>openEdit(a)}><Pencil size={14}/></button>
                      <button className="btn btn-ghost btn-sm btn-icon" style={{color:'var(--danger)'}} title="Delete" onClick={()=>setDelId(a.id)}><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Admin Form Modal */}
      <Modal open={modal==='form'} onClose={()=>setModal(null)} title={editId ? 'Edit Admin' : 'Create Admin'} size="modal-lg">
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full Name <span className="req">*</span></label>
                <input className="form-input" placeholder="John Doe" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required/>
              </div>
              <div className="form-group">
                <label className="form-label">Email <span className="req">*</span></label>
                <input className="form-input" type="email" placeholder="john@example.com" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} required/>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" placeholder="+91 9999999999" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label className="form-label">Password {!editId && <span className="req">*</span>}</label>
                <input className="form-input" type="password" placeholder={editId ? 'Leave blank to keep current' : 'Set password'} value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} required={!editId}/>
              </div>
            </div>
            {editId && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.is_active} onChange={e=>setForm(f=>({...f,is_active:+e.target.value}))}>
                  <option value={1}>Active</option><option value={0}>Inactive</option>
                </select>
              </div>
            )}

            {/* Panel Assignment with multi-select role_types */}
            <div className="form-group">
              <label className="form-label">
                Assign Panels &amp; Roles
                <span style={{color:'var(--text-muted)',fontWeight:400,marginLeft:6,fontSize:'.78rem'}}>
                  (check panel to assign · select one or more role types)
                </span>
              </label>
              <div style={{ display:'grid', gap:8, maxHeight:320, overflowY:'auto', padding:4 }}>
                {panels.filter(p=>p.is_active).map(p => {
                  const pConf   = form.panels.find(x => x.panel_id === p.id);
                  const isChecked = !!pConf;
                  return (
                    <div key={p.id} style={{
                      display:'flex', flexDirection:'column', gap:8,
                      padding:'10px 12px', borderRadius:'var(--r-md)', border:'1px solid',
                      borderColor: isChecked ? p.color : 'var(--border)',
                      background: isChecked ? p.color+'12' : 'var(--bg-base)',
                      transition:'all 150ms',
                    }}>
                      {/* Panel checkbox row */}
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                        <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', flex:1 }}>
                          <input type="checkbox" className="perm-checkbox" checked={isChecked} onChange={()=>togglePanel(p.id)}/>
                          <div style={{ width:10, height:10, borderRadius:'50%', background:p.color, flexShrink:0 }}/>
                          <div>
                            <div style={{ fontSize:'.82rem', fontWeight:600 }}>{p.name}</div>
                            <div style={{ fontSize:'.7rem', color:'var(--text-muted)' }}>{p.code}</div>
                          </div>
                        </label>

                        {/* Multi-select role type checkboxes */}
                        {isChecked && (
                          <div style={{ display:'flex', gap:10, flexShrink:0 }}>
                            {ROLE_TYPE_OPTIONS.map(opt => (
                              <label key={opt.value} style={{ display:'flex', alignItems:'center', gap:4, fontSize:'.75rem', cursor:'pointer', fontWeight:500 }}>
                                <input
                                  type="checkbox"
                                  checked={(pConf.role_types || []).includes(opt.value)}
                                  onChange={() => togglePanelRoleType(p.id, opt.value)}
                                  style={{ cursor:'pointer' }}
                                />
                                {opt.label}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Permissions matrices per selected role */}
                      {isChecked && panelModules[p.id] && panelModules[p.id].length > 0 && permTypes.length > 0 && (
                        (pConf.role_types || []).map(rt => {
                          const rtLabel = ROLE_TYPE_OPTIONS.find(o => o.value === rt)?.label || rt;
                          return (
                            <div key={rt} style={{ borderTop:'1px solid var(--border)', paddingTop:8, marginTop:4 }}>
                              <div style={{ fontSize:'.75rem', fontWeight:600, marginBottom:6, color:'var(--text-muted)' }}>
                                {rtLabel} Permissions Matrix
                              </div>
                              <div style={{ background:'var(--bg-base)', borderRadius:'var(--r-sm)', border:'1px solid var(--border)', overflow:'hidden' }}>
                                <table style={{ margin:0, fontSize:'.75rem' }}>
                                  <thead>
                                    <tr>
                                      <th style={{padding:'6px 8px'}}>Module</th>
                                      {permTypes.map(pt => (
                                        <th key={pt.id} style={{textAlign:'center', minWidth:36}}>{pt.label}</th>
                                      ))}
                                      <th style={{textAlign:'center', width:32}}>All</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {panelModules[p.id].map(m => {
                                      const permKey = `${m.id}_${rt}`;
                                      const grantedIds = form.permissions[permKey] || [];
                                      const allOn = permTypes.length > 0 && permTypes.every(pt => grantedIds.includes(pt.id));
                                      const toggleP = (ptId) => togglePerm(m.id, rt, ptId);
                                      const toggleAll = (enable) => toggleAllPerms(m.id, rt, enable);
                                      return (
                                        <tr key={m.id}>
                                          <td style={{padding:'6px 8px', fontWeight:500}}>{m.module_name}</td>
                                          {permTypes.map(pt => (
                                            <td key={pt.id} style={{textAlign:'center'}}>
                                              <input type="checkbox"
                                                checked={grantedIds.includes(pt.id)}
                                                onChange={() => toggleP(pt.id)}
                                                style={{cursor:'pointer'}} />
                                            </td>
                                          ))}
                                          <td style={{textAlign:'center'}}>
                                            <button type="button" onClick={() => toggleAll(!allOn)}
                                              style={{
                                                width:20, height:20, borderRadius:'var(--r-sm)',
                                                border:`1.5px solid ${allOn ? 'var(--brand)' : 'var(--border)'}`,
                                                background: allOn ? 'var(--brand)' : 'transparent',
                                                cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                                                transition:'all .15s', margin:'0 auto', fontSize:10, color:'white',
                                              }}>
                                              {allOn && '✓'}
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  );
                })}
                {panels.length === 0 && <p className="text-muted text-sm">No active panels. Create panels first.</p>}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={()=>setModal(null)}>Cancel</button>
            <button type="submit" className="btn btn-primary">{editId ? 'Update' : 'Create'} Admin</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog open={!!delId} onClose={()=>setDelId(null)} onConfirm={handleDelete} loading={delLoading}
        message="This admin will be soft-deleted and removed from all active views."/>
    </div>
  );
}
